<script>
  /**
   * Modal/Dialog Component
   * @prop {boolean} open - Whether modal is visible
   * @prop {string} title - Modal title
   * @prop {boolean} closeOnBackdrop - Close when clicking backdrop
   * @prop {boolean} closeOnEscape - Close when pressing Escape
   * @prop {Function} onclose - Callback when modal closes
   */
  import { onMount } from "svelte";

  let {
    open = $bindable(false),
    title = "",
    closeOnBackdrop = true,
    closeOnEscape = true,
    onclose = () => {},
    children,
    actions,
  } = $props();

  let dialogRef = $state(null);

  function handleBackdropClick(e) {
    if (closeOnBackdrop && e.target === e.currentTarget) {
      close();
    }
  }

  function handleKeydown(e) {
    if (closeOnEscape && e.key === "Escape") {
      close();
    }
  }

  function close() {
    open = false;
    onclose();
  }

  $effect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      // Focus trap would go here
    } else {
      document.body.style.overflow = "";
    }
  });

  onMount(() => {
    return () => {
      document.body.style.overflow = "";
    };
  });
</script>

<svelte:window onkeydown={handleKeydown} />

{#if open}
  <div
    class="modal-backdrop"
    onclick={handleBackdropClick}
    role="presentation"
    aria-hidden="true"
  >
    <div
      class="modal-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      bind:this={dialogRef}
    >
      {#if title}
        <header class="modal-header">
          <h2 id="modal-title" class="modal-title">{title}</h2>
        </header>
      {/if}

      <div class="modal-content">
        {@render children?.()}
      </div>

      {#if actions}
        <footer class="modal-actions">
          {@render actions?.()}
        </footer>
      {/if}
    </div>
  </div>
{/if}

<style>
  .modal-backdrop {
    position: fixed;
    inset: 0;
    z-index: var(--z-modal, 1000);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-4);
    background: rgba(15, 23, 42, 0.6);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    animation: fadeIn 0.2s ease-out;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  .modal-dialog {
    background: var(--glass-bg-dark);
    backdrop-filter: blur(var(--blur-lg));
    -webkit-backdrop-filter: blur(var(--blur-lg));
    border: 1px solid var(--glass-border);
    border-radius: var(--radius-2xl);
    box-shadow: var(--shadow-2xl), var(--shadow-glow);
    max-width: 500px;
    width: 100%;
    max-height: calc(100vh - var(--space-8));
    overflow: hidden;
    display: flex;
    flex-direction: column;
    animation: slideUp 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(24px) scale(0.96);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  .modal-header {
    padding: var(--space-5) var(--space-6);
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }

  .modal-title {
    font-size: var(--text-lg);
    font-weight: var(--font-semibold);
    color: var(--color-gray-800);
    margin: 0;
    letter-spacing: var(--tracking-tight);
  }

  .modal-content {
    padding: var(--space-6);
    overflow-y: auto;
    flex: 1;
  }

  .modal-actions {
    padding: var(--space-4) var(--space-6);
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    display: flex;
    justify-content: flex-end;
    gap: var(--space-3);
    background: rgba(248, 250, 252, 0.5);
  }

  @media (max-width: 640px) {
    .modal-backdrop {
      padding: var(--space-3);
      align-items: flex-end;
    }

    .modal-dialog {
      max-height: 85vh;
      border-radius: var(--radius-2xl) var(--radius-2xl) 0 0;
    }

    @keyframes slideUp {
      from {
        opacity: 0;
        transform: translateY(100%);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  }
</style>
